import React from 'react';
import { getApiBase } from '../apiBase';

const AUTOSEND_KEY = 'howto_invoice_autosend';

const inp = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', marginTop: 8,
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10, color: '#fff', fontSize: 14,
};

export default function InvoicingPanel({ ownerId, onClose }) {
  const [autoSend, setAutoSend] = React.useState(() => {
    try { return localStorage.getItem(AUTOSEND_KEY) === 'true'; } catch (_) { return false; }
  });
  const [stripeReady, setStripeReady] = React.useState(false);
  const [testMode, setTestMode] = React.useState(false);

  // Send-invoice form
  const [email, setEmail] = React.useState('');
  const [desc, setDesc] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/owner/payments/status`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setStripeReady(Boolean(data && data.stripeConfigured));
          setTestMode(Boolean(data && data.testMode));
        }
      } catch (_) { /* leave not-ready; backend may be offline */ }
    })();
    return () => { cancelled = true; };
  }, [ownerId]);

  const toggleAutoSend = React.useCallback((e) => {
    const next = e.target.checked;
    setAutoSend(next);
    try { localStorage.setItem(AUTOSEND_KEY, next ? 'true' : 'false'); } catch (_) {}
  }, []);

  const connectStripe = React.useCallback(() => {
    window.open('https://dashboard.stripe.com/apikeys', '_blank');
  }, []);

  const sendInvoice = React.useCallback(async () => {
    if (sending) return; // re-entrancy guard: never fire a second invoice while one is in flight
    setResult(null);
    const amt = Number(amount);
    if (!email.trim() || !desc.trim() || !Number.isFinite(amt) || !(amt > 0)) {
      setResult({ ok: false, msg: 'Enter customer email, a description, and an amount.' });
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${getApiBase()}/api/owner/invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerEmail: email.trim(),
          customerName: email.trim(),
          lineItems: [{ description: desc.trim(), amountUsd: amt }],
          confirm: true,
        }),
      });
      const data = await res.json();
      if (res.ok && data && data.ok) {
        const url = data.hostedUrl || data.hostedInvoiceUrl || data.invoiceUrl
          || (data.invoice && (data.invoice.hosted_invoice_url || data.invoice.url)) || '';
        setResult({ ok: true, msg: data.testMode ? 'Test invoice created.' : 'Invoice sent.', url });
      } else {
        setResult({ ok: false, msg: (data && (data.message || data.error)) || `Failed (${res.status})` });
      }
    } catch (e) {
      setResult({ ok: false, msg: (e && e.message) || 'Could not reach the server. Try again.' });
    } finally {
      setSending(false);
    }
  }, [email, desc, amount, sending]);

  return (
    <div className="agent-detail" onClick={onClose}>
      <div className="agent-detail__panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="agent-detail__close" onClick={onClose} aria-label="Close">×</button>
        <div className="agent-detail__title">Invoicing</div>
        <div className="agent-detail__cadence">Per job</div>
        <p className="agent-detail__desc">
          When a job is done, Aria drafts the invoice and — after you review it — sends it by email and text.
        </p>

        <div className="agent-detail__section">Setup</div>
        {stripeReady ? (
          <div className="agent-detail__status sched-ok">Stripe is connected{testMode ? ' (test mode)' : ''}. Invoicing is ready.</div>
        ) : (
          <div className="sched-warn">Invoicing needs Stripe connected on the server (STRIPE_SECRET_KEY).</div>
        )}
        <button type="button" className="agent-detail__cta" onClick={connectStripe}>
          Connect Stripe
        </button>

        {stripeReady && (
          <>
            <div className="agent-detail__section">Send an invoice</div>
            <input style={inp} type="email" placeholder="Customer email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input style={inp} type="text" placeholder="What it's for (e.g. Full junk haul)" value={desc} onChange={(e) => setDesc(e.target.value)} />
            <input style={inp} type="number" placeholder="Amount (USD)" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <button type="button" className="agent-detail__cta" style={{ marginTop: 10 }} disabled={sending} onClick={sendInvoice}>
              {sending ? 'Sending…' : (testMode ? 'Create test invoice' : 'Send invoice')}
            </button>
            {result && (
              <div className={`agent-detail__status ${result.ok ? 'sched-ok' : 'sched-warn'}`} style={{ marginTop: 10 }}>
                {result.msg}{' '}
                {result.url && <a href={result.url} target="_blank" rel="noreferrer" style={{ color: '#00d9ff' }}>View / pay link</a>}
              </div>
            )}
          </>
        )}

        <div className="agent-detail__section">Sending</div>
        <label className="sched-toggle">
          <input type="checkbox" checked={autoSend} onChange={toggleAutoSend} />
          <span>Auto-send the invoice on completion</span>
        </label>

        <div className="agent-detail__note">
          Default is you review, then tap Send. Turn on auto-send for hands-off.
        </div>
      </div>
    </div>
  );
}
